import { Injectable } from '@angular/core';
import { Query } from '@datorama/akita';
import { isNullish } from '@y42/util/nullish';
import { Observable } from 'rxjs';
import {
  AclResourceType,
  AclRole,
  AclResource,
  AclRoleAssignment,
  AclPermission,
  AclPermissionDetail,
  isAssetModuleType,
  AclPermissionsRecord,
  AclPermissionsBySpaceId,
} from '../models';
import { AclState, AclStore } from './acl.store';

@Injectable({ providedIn: 'root' })
export class AclQuery extends Query<AclState> {
  constructor(store: AclStore) {
    super(store);
  }

  arePermissionsEmpty({ resourceType, resourceId }: { resourceType: AclResourceType; resourceId: string }): boolean {
    const resourceTypePermissions = this.getValue().permissions[resourceType];

    if (!resourceTypePermissions) {
      return true;
    }

    if (resourceType === AclResourceType.Org || resourceType === AclResourceType.Space) {
      return !resourceTypePermissions[resourceId];
    }

    return false;
  }

  getRoles({ resourceType }: { resourceType: AclResourceType }): AclRole[] {
    return this.getValue().roles[resourceType] || [];
  }

  getRoleTooltip({ role, resourceType }: { role: AclRole; resourceType: AclResourceType }): string {
    return this.getValue().roleTooltips[role][resourceType] || '';
  }

  areRolesLoaded(): boolean {
    return Object.keys(this.getValue().roles).length > 0;
  }

  selectEnumeratedResources({ resourceType }: { resourceType: AclResourceType }): Observable<AclResource[]> {
    return this.select((st) => st.resources[resourceType] || []);
  }

  selectRoleAssignments({
    resourceType,
    resourceId,
  }: {
    resourceType: AclResourceType;
    resourceId?: string;
  }): Observable<AclRoleAssignment[]> {
    return this.select((st) => {
      const resourceTypeRoleAssignments = st.roleAssignments[resourceType] || {};
      if (resourceId) {
        return resourceTypeRoleAssignments[resourceId] || [];
      }
      return Object.values(resourceTypeRoleAssignments).flatMap(
        (resourceIdRoleAssignments) => resourceIdRoleAssignments,
      );
    });
  }

  getRoleAssignmentsByResourceId({
    resourceType,
    resourceId,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
  }): AclRoleAssignment[] {
    return this.getValue().roleAssignments[resourceType]?.[resourceId] || [];
  }

  getPermission({
    resourceType,
    resourceId,
    permission,
    spaceId,
  }: {
    resourceType: AclResourceType;
    resourceId: string;
    permission: AclPermission;
    spaceId?: string;
  }): AclPermissionDetail | null {
    const resourcePermissions = this.getValue().permissions[resourceType]?.[resourceId];

    if (!resourcePermissions) {
      return null;
    }

    if (!isAssetModuleType(resourceType)) {
      return (resourcePermissions as AclPermissionsRecord)[permission] ?? null;
    }

    if (isNullish(spaceId)) {
      return null;
    }

    return (resourcePermissions as AclPermissionsBySpaceId)[spaceId]?.[permission] ?? null;
  }

  getResourceIdsByPermission({
    resourceType,
    permission,
    spaceId,
  }: {
    resourceType: AclResourceType;
    permission: AclPermission;
    spaceId?: string;
  }): string[] {
    const resourceTypePermissionsMap = this.getValue().permissions[resourceType];
    if (!resourceTypePermissionsMap) {
      return [];
    }

    return Object.entries(resourceTypePermissionsMap)
      .filter(([_resourceId, resourceTypePermissions]) => {
        if (!isAssetModuleType(resourceType)) {
          return !!(resourceTypePermissions as AclPermissionsRecord)[permission];
        }
        if (isNullish(spaceId)) {
          return false;
        }
        return !!(resourceTypePermissions as AclPermissionsBySpaceId)[spaceId]?.[permission];
      })
      .map(([resourceId]) => resourceId);
  }
}
